// File path: /modules/elevate/frontend/src/services/authService.ts

import { Auth } from 'aws-amplify';
import { apiHelpers } from './api';
import { buildFirstLoginAttributes, selfWritableAttributes } from './cognitoAttributes';
import type { NewPasswordRequiredData, WritableUserAttributes } from './cognitoAttributes';

// The first-login form sends names + preferred_username only (P-HARD H5).
export type { NewPasswordRequiredData, WritableUserAttributes } from './cognitoAttributes';

// Types for authentication
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  tokens: {
    id_token: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
  user: {
    username: string;
    email: string;
    given_name: string;
    family_name: string;
    tenant_id: string;
    role: string;
    groups: string[];
    schoolId?: string;
    districtId?: string;
    programId?: string;
    stateId?: string;
    program_affiliation?: string;
  };
}

// Attributes as READ from Cognito. The custom:* ones are legacy, UI-only values:
// the browser never writes them (P-HARD H5) and the authorizer never reads them.
export interface UserAttributes {
  email: string;
  given_name: string;
  family_name: string;
  'custom:role': string;
  'custom:tenant_id': string;
  'custom:schoolId'?: string;
  'custom:districtId'?: string;
  'custom:programId'?: string;
  'custom:stateId'?: string;
  'custom:program_affiliation'?: string;
}

class AuthService {
  /**
   * Login user with username and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // Use Cognito directly for authentication
      const user = await Auth.signIn(credentials.username, credentials.password);

      // Get the session tokens
      const session = await Auth.currentSession();

      // Get user attributes
      const attributes = await Auth.userAttributes(user);
      const attributeMap: Record<string, string> = {};
      attributes.forEach(attr => {
        attributeMap[attr.Name] = attr.Value;
      });

      // Get user groups
      const groups = user.signInUserSession?.idToken?.payload['cognito:groups'] || [];

      return {
        tokens: {
          id_token: session.getIdToken().getJwtToken(),
          access_token: session.getAccessToken().getJwtToken(),
          refresh_token: session.getRefreshToken().getToken(),
          expires_in: 3600, // Default 1 hour
        },
        user: {
          username: user.username,
          email: attributeMap.email,
          given_name: attributeMap.given_name,
          family_name: attributeMap.family_name,
          tenant_id: attributeMap['custom:tenant_id'] || '',
          role: attributeMap['custom:role'] || '',
          groups: groups,
          schoolId: attributeMap['custom:schoolId'] || undefined,
          districtId: attributeMap['custom:districtId'] || undefined,
          programId: attributeMap['custom:programId'] || undefined,
          stateId: attributeMap['custom:stateId'] || undefined,
        },
      };
    } catch (error: any) {
      if (error.code === 'UserNotConfirmedException') {
        throw new Error('User account is not confirmed. Please check your email.');
      } else if (error.code === 'NotAuthorizedException') {
        throw new Error('Incorrect username or password.');
      } else if (error.code === 'UserNotFoundException') {
        throw new Error('User does not exist.');
      }
      throw error;
    }
  }

  /**
   * Handle new password required challenge.
   *
   * Sends given_name, family_name and preferred_username only — never a role,
   * a tenant or any custom:* attribute (P-HARD H5). Role and organization come
   * from the user's Cognito groups and core.users row, set by an administrator.
   */
  async completeNewPassword(user: any, newPassword: string, requiredAttributes: NewPasswordRequiredData): Promise<LoginResponse> {
    try {
      // Complete the new password challenge
      const updatedUser = await Auth.completeNewPassword(
        user,
        newPassword,
        buildFirstLoginAttributes(requiredAttributes)
      );

      // Get the session after password change
      const session = await Auth.currentSession();

      // Get user attributes
      const attributes = await Auth.userAttributes(updatedUser);
      const attributeMap: Record<string, string> = {};
      attributes.forEach(attr => {
        attributeMap[attr.Name] = attr.Value;
      });

      // Get user groups
      const groups = updatedUser.signInUserSession?.idToken?.payload['cognito:groups'] || [];

      return {
        tokens: {
          id_token: session.getIdToken().getJwtToken(),
          access_token: session.getAccessToken().getJwtToken(),
          refresh_token: session.getRefreshToken().getToken(),
          expires_in: 3600,
        },
        user: {
          username: updatedUser.username,
          email: attributeMap.email,
          given_name: attributeMap.given_name,
          family_name: attributeMap.family_name,
          tenant_id: attributeMap['custom:tenant_id'] || '',
          role: attributeMap['custom:role'] || '',
          groups: groups,
          schoolId: attributeMap['custom:schoolId'] || undefined,
          districtId: attributeMap['custom:districtId'] || undefined,
          programId: attributeMap['custom:programId'] || undefined,
          stateId: attributeMap['custom:stateId'] || undefined,
        },
      };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to set new password');
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await Auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signout fails, we should clear local data
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const attributes = await Auth.userAttributes(user);

      const attributeMap: Record<string, string> = {};
      attributes.forEach(attr => {
        attributeMap[attr.Name] = attr.Value;
      });

      const groups = user.signInUserSession?.idToken?.payload['cognito:groups'] || [];

      return {
        username: user.username,
        email: attributeMap.email,
        given_name: attributeMap.given_name,
        family_name: attributeMap.family_name,
        tenant_id: attributeMap['custom:tenant_id'] || '',
        role: attributeMap['custom:role'] || '',
        schoolId: attributeMap['custom:schoolId'] || '',
        districtId: attributeMap['custom:districtId'] || '',
        programId: attributeMap['custom:programId'] || '',
        stateId: attributeMap['custom:stateId'] || '',
        program_affiliation: attributeMap['custom:program_affiliation'] || '',
        groups: groups,
      };
    } catch (error) {
      throw new Error('Not authenticated');
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<string> {
    try {
      const session = await Auth.currentSession();
      // FIXED: Return Access Token instead of ID Token for API calls
      return session.getAccessToken().getJwtToken();
    } catch (error) {
      throw new Error('Failed to refresh token');
    }
  }

  /**
   * Get Access Token specifically for API calls
   */
  async getAccessToken(): Promise<string> {
    try {
      const session = await Auth.currentSession();
      return session.getAccessToken().getJwtToken();
    } catch (error) {
      throw new Error('Failed to get access token');
    }
  }

  /**
   * Get ID Token specifically for user information
   */
  async getIdToken(): Promise<string> {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      throw new Error('Failed to get ID token');
    }
  }

  /**
   * Forgot password - send reset code
   */
  async forgotPassword(username: string): Promise<void> {
    try {
      await Auth.forgotPassword(username);
    } catch (error: any) {
      if (error.code === 'UserNotFoundException') {
        throw new Error('User does not exist.');
      }
      throw error;
    }
  }

  /**
   * Reset password with code
   */
  async resetPassword(username: string, code: string, newPassword: string): Promise<void> {
    try {
      await Auth.forgotPasswordSubmit(username, code, newPassword);
    } catch (error: any) {
      if (error.code === 'CodeMismatchException') {
        throw new Error('Invalid verification code.');
      } else if (error.code === 'ExpiredCodeException') {
        throw new Error('Verification code has expired.');
      }
      throw error;
    }
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, oldPassword, newPassword);
    } catch (error: any) {
      if (error.code === 'NotAuthorizedException') {
        throw new Error('Incorrect password.');
      }
      throw error;
    }
  }

  /**
   * Update the signed-in user's own attributes: given_name, family_name,
   * phone_number, preferred_username only. Anything else (custom:role,
   * custom:tenant_id, …) is dropped before the call (P-HARD H5).
   */
  async updateUserAttributes(attributes: WritableUserAttributes): Promise<void> {
    const writable = selfWritableAttributes(attributes);
    if (Object.keys(writable).length === 0) {
      return;
    }
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, writable);
    } catch (error) {
      throw new Error('Failed to update user profile');
    }
  }

  /**
   * Validate token with backend and get auth status
   */
  async validateTokenWithBackend(token?: string): Promise<{ valid: boolean; user?: any; message?: string }> {
    try {
      const accessToken = token || await this.getAccessToken();
      const response = await apiHelpers.post<{ valid: boolean; user?: any; message?: string }>('/auth', { token: accessToken });
      return response;
    } catch (error: any) {
      console.error('Token validation failed:', error);
      return {
        valid: false,
        message: error.response?.data?.message || 'Token validation failed'
      };
    }
  }

  /**
   * Get authentication service information
   */
  async getAuthServiceInfo(): Promise<any> {
    try {
      // Call without token to get service info
      const response = await apiHelpers.post('/auth', {});
      return response;
    } catch (error: any) {
      console.error('Failed to get auth service info:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new AuthService();